import { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import type { Tenant } from "@/types";
import { useTenant, useTenants } from "@/store/tenant";
import { useUsers } from "@/store/user";
import { useApplications } from "@/store/application";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { TabBar } from "@/components/ui/TabBar";
import { InfoGrid } from "@/components/ui/InfoGrid";
import { useLayout } from "@/contexts/layout-context";
import {
  Shield,
  AppWindow,
  Users2,
  Users,
  Search,
  Globe,
  // Activity,
  Copy,
  ChevronRight,
  Key,
  Plus,
  Settings,
  MoreVertical,
  UserCheck,
} from "lucide-react";

import { AssignmentsTab } from "./components/AssignmentsTab";
import { TenantIdPsTab } from "./components/tenant-idps-tab";
import { TenantDomainsTab } from "./components/tenant-domains-tab";
import { TenantDetailsTab } from "./components/tenant-details-tab";
import { TenantKeysTab } from "./components/tenant-keys-tab";
import { TenantOnboardingTab } from "./components/tenant-onboarding-tab";


export const ManageTenantPage = () => {
  const { id } = useParams<{ id: string }>();
  const { setBreadcrumbs } = useLayout();
  const tenantStore = useTenant(id);
  const allUsers = useUsers((state) => state.users);
  const allApps = useApplications((state) => state.applications);
  const stats = useTenants((state) => id ? state.tenantScoped[id]?.stats : null);
  const totalUsers = stats?.users_by_status ? Object.values(stats.users_by_status).reduce((a, b) => a + b, 0) : 0;
  const totalApps = stats?.apps_by_status ? Object.values(stats.apps_by_status).reduce((a, b) => a + b, 0) : 0;

  const listAllUsers = () => useUsers.getState().list();
  const listAllApps = () => useApplications.getState().list();

  const tabItems = [
    { id: "details", label: "Details", icon: Settings },
    { id: "onboarding", label: "Onboarding", icon: UserCheck },
    { id: "keys", label: "Keys", icon: Key },
    // { id: "assignments", label: "Assignments", icon: Key },
    { id: "apps", label: "Applications", icon: AppWindow },
    { id: "idps", label: "Identity Providers", icon: Globe },
    { id: "domains", label: "Domains", icon: Globe },
    // { id: "owners", label: "Owners", icon: Shield },
    { id: "users", label: "Users", icon: Users2 },
    // { id: "groups", label: "Groups", icon: Users },
    // { id: "analytics", label: "Analytics", icon: Activity },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab = tabItems.some((t) => t.id === requestedTab)
    ? (requestedTab as string)
    : "details";
  const setActiveTab = (tab: string) => setSearchParams({ tab }, { replace: true });

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const tenantUsers = tenantStore.users.data;
  const tenantGroups = tenantStore.groups.data;
  const allGroups = tenantStore.groups.available;
  const tenantApps = tenantStore.apps.data;

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<
    "users" | "groups" | "idps" | "apps" | null
  >(null);
  const [modalValue, setModalValue] = useState<string>("");
  const [showSystemGroups, setShowSystemGroups] = useState<boolean>(false);

  useEffect(() => {
    if (!id) return;

    useTenants
      .getState()
      .get(id)
      .then((loadedTenant) => {
        setTenant(loadedTenant);
        // We clear layout breadcrumbs because we render them inside the PageHeader now
        setBreadcrumbs([]);
        // Update browser tab title to reflect current tenant
        document.title = `Tenant Details — ${loadedTenant.name}`;
      })
      .catch(console.error);
  }, [id, setBreadcrumbs]);

  useEffect(() => {
    if (!id) return;

    if (activeTab === "users" || activeTab === "owners") {
      Promise.all([listAllUsers(), tenantStore.users.list()]).catch(
        console.error,
      );
    }

    if (activeTab === "groups") {
      Promise.all([
        tenantStore.groups.list(),
        tenantStore.groups.listAvailable(),
      ]).catch(console.error);
    }

    if (activeTab === "idps") {
      Promise.all([
        tenantStore.idps.list(),
        tenantStore.idps.listAvailable(),
      ]).catch(console.error);
    }

    if (activeTab === "apps") {
      Promise.all([listAllApps(), tenantStore.apps.list()]).catch(
        console.error,
      );
    }

    if (activeTab === "domains") {
      tenantStore.domains.list().catch(console.error);
    }

    if (activeTab === "details") {
      Promise.all([
        tenantStore.domains.list(),
        tenantStore.idps.list(),
        tenantStore.idps.listAvailable(),
        useTenants.getState().getStats(id)
      ]).catch(console.error);
    }
  }, [activeTab, id]);

  // helpers for users/groups
  const addUser = async (userId: string) => {
    if (!id) return;
    toast.promise(tenantStore.users.create(userId), {
      loading: "Adding user...",
      success: "User added",
      error: "Failed to add user",
    });
  };
  const addGroup = async (groupId: string) => {
    if (!id) return;
    toast.promise(tenantStore.groups.create(groupId), {
      loading: "Adding group...",
      success: "Group added",
      error: "Failed to add group",
    });
  };
  const removeGroup = async (groupId: string) => {
    if (!id) return;
    toast.promise(tenantStore.groups.delete(groupId), {
      loading: "Removing group...",
      success: "Group removed",
      error: "Failed to remove group",
    });
  };

  const toggleApp = async (appId: string) => {
    if (!id) return;
    const isEnabled = tenantApps.includes(appId);
    const action = isEnabled
      ? tenantStore.apps.disable(appId)
      : tenantStore.apps.enable(appId);

    toast.promise(action, {
      loading: "Updating application...",
      success: "Application updated",
      error: "Failed to update application",
    });
  };

  const nonTenantUsers = allUsers?.filter(
    (u) => !tenantUsers.some((tu) => tu.id === u.id),
  );
  const nonTenantGroups = allGroups?.filter(
    (g) => !tenantGroups.some((tg) => tg.id === g.id),
  );
  const nonTenantApps = allApps?.filter(
    (app) => !tenantApps.includes(app.id)
  );

  const visibleTenantGroups = tenantGroups.filter(
    (g) => showSystemGroups || !g.is_system_managed,
  );

  if (!tenant) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full animate-in fade-in duration-500 pb-20">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            {tenant.logo_url && (
              <img
                src={tenant.logo_url}
                alt={tenant.name}
                className="w-9 h-9 rounded-lg object-contain bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-0.5 shadow-sm shrink-0"
              />
            )}
            <span>
              {tenant.name}{" "}
              <button
                className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors p-1 align-middle"
                onClick={() => {
                  navigator.clipboard.writeText(tenant.slug);
                  toast.success("Tenant Slug copied");
                }}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </span>
          </span>
        }
        breadcrumbs={
          <>
            <Link
              to="/m/tenants"
              className="hover:text-(--primary) transition-colors"
            >
              Tenants
            </Link>
            <ChevronRight className="w-3 h-3 text-slate-400" />
            <span className="text-slate-900 dark:text-gray-100 font-medium">
              {tenant.name}
            </span>
          </>
        }
        subtitle={
          <div className="flex items-center gap-2 mt-1">
            <span className="text-slate-400">
              ID:{" "}
              <span className="text-slate-500 font-mono tracking-tight">
                {tenant.id}
              </span>
            </span>
            <button
              className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors p-1"
              onClick={() => {
                navigator.clipboard.writeText(tenant.id);
                toast.success("Tenant ID copied");
              }}
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        }
        actions={<></>}
      />

      <TabBar
        tabs={tabItems}
        activeTab={activeTab}
        onChange={setActiveTab}
        className="mb-8"
      />

      <div className="mt-8">
        {activeTab === "details" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <TenantDetailsTab tenant={tenant} />

            <div className="space-y-6">
              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] border border-slate-200/60 dark:border-slate-800/60 flex flex-col h-full">
                <div className="p-6 grow">
                  <h3 className="text-[15px] font-bold text-slate-900 dark:text-white mb-6">
                    Quick Stats
                  </h3>
                  <div className="space-y-6">
                    {/* Users count by status */}
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between items-end mb-2">
                          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            Users Breakdown
                          </span>
                          <span className="text-sm font-bold text-slate-900 dark:text-white">
                            Total: {totalUsers}
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1 overflow-hidden">
                          <div
                            className="bg-orange-500 h-1 rounded-full"
                            style={{ width: totalUsers ? "100%" : "0%" }}
                          ></div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800/80 rounded-xl flex flex-col">
                          <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                            Active
                          </span>
                          <span className="text-lg font-bold text-slate-800 dark:text-slate-100 mt-1">
                            {stats?.users_by_status?.active ?? 0}
                          </span>
                        </div>

                        <div className="p-3 bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800/80 rounded-xl flex flex-col">
                          <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                            Pending
                          </span>
                          <span className="text-lg font-bold text-slate-800 dark:text-slate-100 mt-1">
                            {stats?.users_by_status?.pending ?? 0}
                          </span>
                        </div>

                        <div className="p-3 bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800/80 rounded-xl flex flex-col">
                          <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                            Inactive
                          </span>
                          <span className="text-lg font-bold text-slate-800 dark:text-slate-100 mt-1">
                            {stats?.users_by_status?.inactive ?? 0}
                          </span>
                        </div>

                        <div className="p-3 bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800/80 rounded-xl flex flex-col">
                          <span className="text-[10px] font-bold text-red-600 dark:text-red-400 uppercase tracking-wider flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                            Suspended
                          </span>
                          <span className="text-lg font-bold text-slate-800 dark:text-slate-100 mt-1">
                            {stats?.users_by_status?.suspended ?? 0}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-slate-100 dark:border-slate-800/50 my-6"></div>

                    {/* Applications count by status */}
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between items-end mb-2">
                          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                            Applications Breakdown
                          </span>
                          <span className="text-sm font-bold text-slate-900 dark:text-white">
                            Total: {totalApps}
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-1 overflow-hidden">
                          <div
                            className="bg-emerald-500 h-1 rounded-full"
                            style={{ width: totalApps ? "100%" : "0%" }}
                          ></div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        <div className="p-2.5 bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800/80 rounded-xl flex flex-col items-center text-center">
                          <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-1">
                            Active
                          </span>
                          <span className="text-base font-bold text-slate-800 dark:text-slate-100 mt-1">
                            {stats?.apps_by_status?.active ?? 0}
                          </span>
                        </div>

                        <div className="p-2.5 bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800/80 rounded-xl flex flex-col items-center text-center">
                          <span className="text-[9px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1">
                            Inactive
                          </span>
                          <span className="text-base font-bold text-slate-800 dark:text-slate-100 mt-1">
                            {stats?.apps_by_status?.inactive ?? 0}
                          </span>
                        </div>

                        <div className="p-2.5 bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-800/80 rounded-xl flex flex-col items-center text-center">
                          <span className="text-[9px] font-bold text-red-600 dark:text-red-400 uppercase tracking-wider flex items-center gap-1">
                            Suspended
                          </span>
                          <span className="text-base font-bold text-slate-800 dark:text-slate-100 mt-1">
                            {stats?.apps_by_status?.suspended ?? 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "onboarding" && (
          <TenantOnboardingTab
            tenant={tenant}
            onUpdate={(updated) => setTenant(updated)}
          />
        )}
        {activeTab === "keys" && <TenantKeysTab tenant={tenant} />}
        {activeTab === "assignments" && <AssignmentsTab tenant={tenant} />}

        {activeTab === "idps" && <TenantIdPsTab tenantId={tenant.id} />}
        {activeTab === "domains" && <TenantDomainsTab tenantId={tenant.id} />}
        {activeTab === "groups" && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Groups</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage groups within this tenant.</p>
              </div>
              <div className="flex gap-3 items-center">
                <label className="flex items-center space-x-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-orange-500 focus:ring-orange-500/50 dark:border-slate-600 dark:bg-slate-800"
                    checked={showSystemGroups}
                    onChange={(e) => setShowSystemGroups(e.target.checked)}
                  />
                  <span className="text-slate-600 dark:text-slate-400">Show System Managed</span>
                </label>
                <Button
                  onClick={() => {
                    setModalType("groups");
                    setModalValue("");
                    setModalOpen(true);
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white shadow-md border-0 gap-2 px-4 rounded-lg"
                >
                  <Plus className="w-4 h-4" /> Add Group
                </Button>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800/60 rounded-xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] overflow-hidden">
              {visibleTenantGroups.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-slate-500 dark:text-slate-400">No groups in this tenant.</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                    Add groups to organize your users
                  </p>
                </div>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50/80 dark:bg-slate-900/50 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100/60 dark:border-slate-800/60">
                      <th className="px-6 py-3">Group Name</th>
                      <th className="px-6 py-3">Description</th>
                      <th className="px-6 py-3 text-center">Members</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100/60 dark:divide-slate-800/60">
                    {visibleTenantGroups.map((g) => (
                      <tr
                        key={g.id}
                        className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all"
                      >
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded shrink-0 bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-orange-500 border border-slate-100 dark:border-slate-800">
                              <Shield className="w-4 h-4" />
                            </div>
                            <span className="text-[13px] font-bold text-slate-900 dark:text-white">
                              {g.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-3 text-[13px] text-slate-500 dark:text-slate-400">
                          {g.description || "Enterprise platform access"}
                        </td>
                        <td className="px-6 py-3 text-[13px] font-bold text-slate-700 dark:text-slate-300 text-center">
                          {""}
                        </td>
                        <td className="px-6 py-3">
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
                            Active
                          </span>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <button
                            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-md transition-colors"
                            onClick={() => removeGroup(g.id)}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {visibleTenantGroups.length > 0 && (
                <div className="px-6 py-3 border-t border-slate-100/60 dark:border-slate-800/60 flex items-center justify-between text-sm text-slate-500 bg-slate-50/30 dark:bg-slate-950/30 text-[12px] font-medium">
                  <span>
                    Showing 1 to {visibleTenantGroups.length} of {visibleTenantGroups.length}{" "}
                    groups
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white dark:bg-slate-900 text-slate-400 border-slate-200 h-8 px-4 rounded-md shadow-sm"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border-slate-200 h-8 px-4 rounded-md shadow-sm font-medium"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {(activeTab === "users" || activeTab === "owners") && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white capitalize">{activeTab}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage {activeTab} within this tenant.</p>
              </div>
              <div className="flex gap-3 items-center">
                {activeTab === "users" && (
                  <Button
                    onClick={() => {
                      setModalType("users");
                      setModalValue("");
                      setModalOpen(true);
                    }}
                    className="bg-orange-500 hover:bg-orange-600 text-white shadow-md border-0 gap-2 px-4 rounded-lg"
                  >
                    <Plus className="w-4 h-4" /> Add User
                  </Button>
                )}
              </div>
            </div>
            <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800/60 rounded-xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] overflow-hidden">
              {tenantUsers.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    {activeTab === "owners" ? <Shield className="w-8 h-8 text-slate-400" /> : <Users2 className="w-8 h-8 text-slate-400" />}
                  </div>
                  <p className="text-slate-500 dark:text-slate-400">No {activeTab} in this tenant.</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                    Add {activeTab} to grant access to this tenant
                  </p>
                </div>
              ) : (
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-slate-50/80 dark:bg-slate-900/50 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100/60 dark:border-slate-800/60">
                      <th className="px-6 py-3">
                        {activeTab === "owners" ? "Owner Name" : "User Name"}
                      </th>
                      <th className="px-6 py-3">Email Address</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3">Last Active</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100/60 dark:divide-slate-800/60">
                    {tenantUsers.map((u) => {
                      const initials = (u.name || "U")
                        .split(" ")
                        .map((n: string) => n[0])
                        .join("")
                        .substring(0, 2)
                        .toUpperCase();
                      const statusMap: Record<number, string> = {
                        0: "Pending",
                        1: "Active",
                        2: "Inactive",
                        3: "Suspended",
                      };
                      const statusText = u.status !== undefined ? (statusMap[u.status] ?? "Unknown") : "Active";
                      const statusColor =
                        u.status === 0
                          ? "bg-amber-500"
                          : u.status === 2
                          ? "bg-slate-400"
                          : u.status === 3
                          ? "bg-red-500"
                          : "bg-emerald-500";
                      const statusBg =
                        u.status === 0
                          ? "bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-400 border-amber-200 dark:border-amber-500/20"
                          : u.status === 2
                          ? "bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700"
                          : u.status === 3
                          ? "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400 border-red-200 dark:border-red-500/20"
                          : "bg-emerald-50 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20";

                      return (
                        <tr
                          key={u.id}
                          className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all"
                        >
                          <td className="px-6 py-3">
                            <div className="flex items-center gap-4">
                              <div className="w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center text-orange-600 dark:text-orange-400 font-bold text-sm shrink-0">
                                {initials}
                              </div>
                              <Link
                                to={`/m/users/${u.id}`}
                                className="text-[13px] font-bold text-slate-900 dark:text-white hover:text-orange-500 transition-colors"
                              >
                                {u.name || (u.email && u.email.split("@")[0])}
                              </Link>
                            </div>
                          </td>
                          <td className="px-6 py-3 text-[13px] text-slate-500 dark:text-slate-400">
                            {u.email}
                          </td>
                          <td className="px-6 py-3">
                            <span
                              className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border flex items-center gap-1.5 w-max ${statusBg}`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${statusColor}`}
                              ></span>
                              {statusText}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-[13px] text-slate-400">
                            {u.last_active ? new Date(u.last_active).toLocaleDateString() : ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {tenantUsers.length > 0 && (
                <div className="px-6 py-3 border-t border-slate-100/60 dark:border-slate-800/60 flex items-center justify-between text-sm text-slate-500 bg-slate-50/30 dark:bg-slate-950/30 text-[12px] font-medium">
                  <span>
                    Showing 1 to {tenantUsers.length} of {tenantUsers.length}{" "}
                    {activeTab}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white dark:bg-slate-900 text-slate-400 border-slate-200 h-8 px-4 rounded-xl shadow-sm"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border-slate-200 h-8 w-8 p-0! rounded-xl shadow-sm font-medium flex items-center justify-center"
                    >
                      1
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white border-slate-200 h-8 px-4 rounded-xl shadow-sm font-medium"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "apps" && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Enabled Applications</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Control which platform applications this tenant is authorized to use.</p>
              </div>
              <div className="flex gap-3 items-center">
                <Button
                  onClick={() => {
                    setModalType("apps");
                    setModalValue("");
                    setModalOpen(true);
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white shadow-md border-0 gap-2 px-4 rounded-lg"
                >
                  <Plus className="w-4 h-4" /> Link App
                </Button>
              </div>
            </div>

            {allApps.length === 0 ? (
              <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800/60 rounded-xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] overflow-hidden">
                <div className="py-12 text-center">
                  <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AppWindow className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-slate-500 dark:text-slate-400">No Applications defined globally.</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                    Applications can be linked once they are created
                  </p>
                </div>
              </div>
            ) : (
              <InfoGrid cols={3}>
                {allApps.map((app) => {
                  const isEnabled = tenantApps.includes(app.id);
                  return (
                    <Card
                      key={app.id}
                      className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800/60 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] hover:border-orange-500/30 transition-all group flex flex-col h-full"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-slate-50 dark:bg-slate-800/50 flex items-center justify-center border border-slate-100 dark:border-slate-800 transform group-hover:scale-110 transition-transform duration-300 overflow-hidden">
                          {app.logo_url ? (
                            <img src={app.logo_url} alt={app.name} className="w-full h-full object-cover" />
                          ) : (
                            <AppWindow className="w-6 h-6 text-slate-400 group-hover:text-orange-500 transition-colors" />
                          )}
                        </div>
                        <div
                          className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${isEnabled ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20" : "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400 border-red-200 dark:border-red-500/20"}`}
                        >
                          {isEnabled ? "Enabled" : "Disabled"}
                        </div>
                      </div>
                      <div className="mb-6 flex-1 min-h-[70px]">
                        <h3 className="text-[15px] font-bold text-slate-900 dark:text-white leading-tight mb-1">
                          {app.name}
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1 line-clamp-2">
                          {app.description || "Platform Service"}
                        </p>
                        {app.base_url && (
                          <a
                            href={app.base_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-orange-500 hover:underline mt-2 block truncate font-semibold"
                          >
                            {app.base_url}
                          </a>
                        )}
                      </div>

                      <div className="flex justify-between items-center pt-4 border-t border-slate-100/60 dark:border-slate-800/60 mt-auto">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                          Service Status
                        </span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={isEnabled}
                            onChange={() => toggleApp(app.id)}
                          />
                          <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-slate-600 peer-checked:bg-orange-500 shadow-inner"></div>
                        </label>
                      </div>
                    </Card>
                  );
                })}
              </InfoGrid>
            )}
          </div>
        )}

        {activeTab === "analytics" && (
          <div className="p-12 text-center text-slate-500 bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800/60 rounded-xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)]">
            Content for <span className="capitalize">{activeTab}</span> will be
            implemented shortly.
          </div>
        )}
      </div>

      {/* generic add-modal */}
      {modalOpen && modalType && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-50 p-4 transition-all animate-in fade-in">
          <Card
            className="max-w-md w-full shadow-2xl scale-in-center overflow-hidden"
            title={
              modalType === "users"
                ? "Assign User"
                : modalType === "groups"
                  ? "Assign Group"
                  : "Configure"
            }
            subtitle={`Link existing ${modalType} to ${tenant.name}.`}
          >
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Selection
                </label>
                {modalType === "users" && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <select
                      className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-(--primary)/10 focus:border-(--primary) transition-all font-medium text-slate-900 dark:text-white w-full outline-none appearance-none"
                      value={modalValue}
                      onChange={(e) => setModalValue(e.target.value)}
                    >
                      <option value="" disabled>
                        Search users...
                      </option>
                      {nonTenantUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.email || u.name || u.id}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {modalType === "groups" && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <select
                      className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-(--primary)/10 focus:border-(--primary) transition-all font-medium text-slate-900 dark:text-white w-full outline-none appearance-none"
                      value={modalValue}
                      onChange={(e) => setModalValue(e.target.value)}
                    >
                      <option value="" disabled>
                        Search groups...
                      </option>
                      {nonTenantGroups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name || g.id}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {modalType === "apps" && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <select
                      className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-(--primary)/10 focus:border-(--primary) transition-all font-medium text-slate-900 dark:text-white w-full outline-none appearance-none"
                      value={modalValue}
                      onChange={(e) => setModalValue(e.target.value)}
                    >
                      <option value="" disabled>
                        Search applications...
                      </option>
                      {nonTenantApps.map((app) => (
                        <option key={app.id} value={app.id}>
                          {app.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-800">
                <Button variant="ghost" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  disabled={!modalValue}
                  onClick={async () => {
                    if (id && modalType && modalValue) {
                      if (modalType === "users") await addUser(modalValue);
                      if (modalType === "groups") await addGroup(modalValue);
                      if (modalType === "apps") {
                        await toast.promise(tenantStore.apps.enable(modalValue), {
                          loading: "Linking application...",
                          success: "Application linked successfully",
                          error: "Failed to link application",
                        });
                      }
                    }
                    setModalOpen(false);
                  }}
                >
                  Confirm Assignment
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
